import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../styles/RuntimeLogs.css';
import { fetchLogsTail, fetchLogsInventory } from '../utils/logsApi';
import { NetworkCellRenderer, EntityCellRenderer } from '../utils/metrics';
import { getApiBase } from '../utils/apiBase';
import { ReactComponent as ArrowDownIcon } from '../assets/arrow-narrow-down.svg';

function SeverityBadge({ level }) {
  const raw = (level || '').toString().trim().toLowerCase();
  let label = 'N/A';
  if (raw === 'warn' || raw === 'warning') label = 'WARNING';
  else if (raw === 'error' || raw === 'err') label = 'ERROR';
  else if (raw === 'info') label = 'INFO';
  else if (raw) label = raw.toUpperCase();
  // Map to standard pill classes
  // info -> green, warn -> amber, error -> red
  let statusClass = 'status-pending';
  if (raw === 'info') statusClass = 'status-success';
  else if (raw === 'warn' || raw === 'warning') statusClass = 'status-late';
  else if (raw === 'error' || raw === 'err') statusClass = 'status-missing';
  return (
    <div className="status-cell-wrapper">
      <div className={`status-cell ${statusClass}`}>{label}</div>
    </div>
  );
}

const parseSeverity = (line) => {
  try {
    const raw = line?.raw || '';
    const s = raw.toLowerCase();
    
    // Special-case: treat non-fatal errors as warnings
    if (/non[-\s]?fatal\s*error/.test(s)) return 'warn';
    
    // Health-monitor-server specific: don't classify [MODULE][ERR] as errors
    if (/^\[?[a-z_0-9]+\]?(?:\[err\])/i.test(raw)) return 'info';
    
    const idx = raw.indexOf('{');
    if (idx >= 0) {
      try {
        const obj = JSON.parse(raw.slice(idx));
        const lvl = (obj.level || obj.severity || '').toString().toLowerCase();
        if (lvl) return lvl.includes('error') ? 'error' : lvl.includes('warn') ? 'warn' : 'info';
      } catch {}
    }
    if (/(\berror\b|\berr\b|\bexception\b)/.test(s)) return 'error';
    if (/(\bwarn\b|\bwarning\b)/.test(s)) return 'warn';
    return 'info';
  } catch { return 'info'; }
};

const stripAnsi = (s) => String(s || '').replace(/\x1b\[[0-9;]*m/g, '');

const extractSource = (s) => {
  const text = stripAnsi(s);
  let m = text.match(/([A-Za-z0-9_\/.\-]+\.go:\d+)/);
  if (m && m[1]) return m[1];
  m = text.match(/([A-Za-z0-9_\/.\-]+\.(py|js|ts):\d+)/);
  if (m && m[1]) return m[1];
  // Try bracketed module tag [module/sub]
  m = text.match(/\[([A-Za-z0-9_\-/.]+)\]/g);
  if (m && m.length > 0) {
    const last = m[m.length - 1];
    return last.replace(/^[\[]|[\]]$/g, '');
  }
  // Fallback: take first token up to two words
  const token = text.split(/\s+/)[0] || '';
  return token || '-';
};

export default function RuntimeLogDetail() {
  const { instanceId, container } = useParams();
  const navigate = useNavigate();
  const [lines, setLines] = useState([]);
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState('all'); // all | errors | warnings
  const gridApiRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const [pendingLines, setPendingLines] = useState([]);
  const isAtBottomRef = useRef(true);
  const [meta, setMeta] = useState({ chain: '', entity: '' });
  const [inventory, setInventory] = useState([]);
  const [entityList, setEntityList] = useState([]);
  const [chainList, setChainList] = useState([]);
  const [instanceList, setInstanceList] = useState([]);
  const [containerList, setContainerList] = useState([]);
  const [isEntityOpen, setIsEntityOpen] = useState(false);
  const [isChainOpen, setIsChainOpen] = useState(false);
  const [isInstanceOpen, setIsInstanceOpen] = useState(false);
  const [isContainerOpen, setIsContainerOpen] = useState(false);
  const [selEntity, setSelEntity] = useState('');
  const [selChain, setSelChain] = useState('');
  const [selInstance, setSelInstance] = useState('');
  const [selContainer, setSelContainer] = useState('');
  const [viewSuspended, setViewSuspended] = useState(false);
  const [stickyContainer, setStickyContainer] = useState('');
  const placeholderMsg = useMemo(() => {
    if (!selInstance) return 'Select an instance to continue';
    if (!selContainer) return 'Select a container to view logs';
    return 'Select an instance and container to view logs';
  }, [selInstance, selContainer]);

  const currentContainer = useMemo(() => {
    try { return decodeURIComponent(container); } catch { return container; }
  }, [container]);

  const formatTs = useCallback((ts) => {
    try {
      const d = new Date(ts);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      const ms = String(d.getMilliseconds()).padStart(3, '0');
      return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}.${ms}`;
    } catch { return ts || ''; }
  }, []);

  const parseRow = useCallback((l) => {
    const level = parseSeverity(l);
    const raw0 = String(l?.raw || '');
    let message = stripAnsi(raw0);
    
    // Filter out empty messages or timestamp-only messages
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return null; // Empty message
    
    // Check if message is just a timestamp (ISO format or similar)
    const timestampPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/;
    if (timestampPattern.test(trimmedMessage)) return null; // Timestamp-only message
    
    // Remove timestamp from beginning of message if it exists
    const timestampPrefixPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?\s*/;
    const bracketTimestampPattern = /^\[\d{2}-\d{2}\|\d{2}:\d{2}:\d{2}\.\d{3}\]\s*/;
    // Remove NEST format: [Nest] 1  - 08/26/2025, 10:36:48 AM (note the extra spaces)
    const nestTimestampPattern = /^\[Nest\]\s*\d+\s*-\s*\d{2}\/\d{2}\/\d{4},\s*\d{1,2}:\d{2}:\d{2}\s*(AM|PM)\s*/;
    
    message = message.replace(timestampPrefixPattern, '').replace(bracketTimestampPattern, '').replace(nestTimestampPattern, '');
    
    // Custom parsing for price-collector containers
    if (container && container.includes('price-collector')) {
      // Remove "FETCHING" prefix but keep the rest
      message = message.replace(/^FETCHING\s+\|\s*/, '');
      
      // Handle multi-line error messages - normalize whitespace but keep on one line
      if (message.includes('CLIENT ERROR') || message.includes('BAD REQUEST') || message.includes('HTTPS://')) {
        // Join broken lines and normalize whitespace
        message = message.replace(/\s+/g, ' ').replace(/\n/g, ' ').trim();
        return { seq: l.seq, time: formatTs(l.ts), level: 'error', message: `ERROR | ${message}` };
      }
      
      // Handle response codes - clean up JSON formatting
      if (message.includes('{"CODE"') || message.includes('RESPONSE:')) {
        try {
          // Extract JSON part
          const jsonMatch = message.match(/\{"CODE"[^}]+\}/);
          if (jsonMatch) {
            const jsonObj = JSON.parse(jsonMatch[0]);
            return { seq: l.seq, time: formatTs(l.ts), level: 'info', message: `RESPONSE | Code: ${jsonObj.CODE}, Message: ${jsonObj.MSG || 'N/A'}` };
          }
        } catch {}
        // Fallback for malformed JSON
        return { seq: l.seq, time: formatTs(l.ts), level: 'info', message: `RESPONSE | ${message}` };
      }
      
      // Handle status messages
      if (message.match(/^(UNABLE TO FETCH|SUCCESS|DATABASE SAVES)/i)) {
        const cleanMessage = message.replace(/\s+/g, ' ').trim();
        const messageLevel = message.includes('UNABLE') ? 'error' : 'info';
        return { seq: l.seq, time: formatTs(l.ts), level: messageLevel, message: `STATUS | ${cleanMessage}` };
      }
      
      // Handle chain-specific success messages
      if (message.match(/^(FLARE:|SONGBIRD:)/i)) {
        const cleanMessage = message.replace(/\s+/g, ' ').trim();
        return { seq: l.seq, time: formatTs(l.ts), level: 'info', message: `CHAIN | ${cleanMessage}` };
      }
      
      // Handle "DATA FOR" entries - keep them but format nicely
      if (message.startsWith('DATA FOR')) {
        return { seq: l.seq, time: formatTs(l.ts), level: 'info', message: `DATA | ${message}` };
      }
      
      // Handle individual data fields - keep them but group under DATA prefix
      if (message.match(/^(PROVIDERID:|STARTCURRENCYID:|ENDCURRENCYID:|PRICE:|BID:|BIDQUANTITY:|ASK:|ASKQUANTITY:|PROVIDERUPDATEDAT:|SAVEDAT:)/i)) {
        return { seq: l.seq, time: formatTs(l.ts), level: 'info', message: `FIELD | ${message}` };
      }
    }
    
    // Parse structured logs: INFO | SOURCE | CONTENT
    // Handle multiple formats:
    // 1. "INFO SOURCE CONTENT" (Go format)
    // 2. "WARN [SOURCE] CONTENT" (NEST format)
    const structuredLogPattern1 = /^(INFO|WARN|WARNING|ERROR|DEBUG|LOG)\s+([A-Z_/]+(?:\.[A-Z_]+)*(?:\.GO:\d+)?)\s+(.+)$/i;
    const structuredLogPattern2 = /^(INFO|WARN|WARNING|ERROR|DEBUG|LOG)\s+\[([A-Z_/]+[A-Z_/]*)\]\s+(.+)$/i;
    
    let structuredMatch = message.match(structuredLogPattern1) || message.match(structuredLogPattern2);
    
    if (structuredMatch) {
      const [, logLevel, source, content] = structuredMatch;
      // Format as: SOURCE | CONTENT (level is already shown in separate column)
      message = `${source}|${content}`;
    }
    
    // Prefer showing the tail of very long messages: trim head and prefix ellipsis
    try {
      const approxCharsPerLine = 180; // keep in sync with getRowHeight
      const maxChars = approxCharsPerLine * 6; // 6 lines worth
      if (message.length > maxChars) {
        message = `… ${message.slice(-maxChars)}`;
      }
    } catch {}
    return { seq: l.seq, time: formatTs(l.ts), level, message };
  }, [formatTs, container]);

  const filteredLines = useMemo(() => {
    try {
      if (severity === 'errors') return (lines || []).filter((l) => parseSeverity(l) === 'error');
      if (severity === 'warnings') return (lines || []).filter((l) => parseSeverity(l) === 'warn');
      return lines || [];
    } catch { return lines || []; }
  }, [lines, severity]);

  // Custom formatting for health-monitor-server logs
  const formatHealthMonitorLogs = useCallback((logLines, parseRowFn) => {
    return logLines.map(parseRowFn).filter(row => row !== null).map(row => {
      if (!row || !row.message) return row;
      
      let message = row.message;
      
      // Handle health-monitor-server log format: [MODULE][ERR] or [MODULE] followed by content
      // Also handle SUBMIT2[ERR] format
      const healthMonitorPattern = /^\[?([A-Z_0-9]+)\]?(?:\[ERR\])?\s*(.*)$/i;
      const match = message.match(healthMonitorPattern);
      
      if (match) {
        const [, module, content] = match;
        
        // Clean up the content by removing timestamp and log level
        let cleanContent = content.trim();
        
        // More aggressive approach: find the actual message content after timestamp and log level
        // Pattern: "2025-08-26 13:17:44,352 - INFO - ACTUAL_MESSAGE" (note: comma in timestamp)
        const timestampLogPattern = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}[,\.]\d{3}\s*-\s*(INFO|WARN|ERROR|DEBUG)\s*-\s*(.*)$/i;
        const timestampMatch = cleanContent.match(timestampLogPattern);
        
        if (timestampMatch) {
          // If we find the pattern, use only the actual message part
          cleanContent = timestampMatch[2];
        } else {
          // Fallback: try to remove timestamp and log level separately (handle both comma and period)
          cleanContent = cleanContent.replace(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}[,\.]\d{3}\s*/i, '');
          cleanContent = cleanContent.replace(/^-\s*(INFO|WARN|ERROR|DEBUG)\s*-\s*/i, '');
          cleanContent = cleanContent.replace(/^(INFO|WARN|ERROR|DEBUG)\s*-\s*/i, '');
          cleanContent = cleanContent.replace(/^\s*-\s*/, '');
        }
        
        // Format as MODULE | CONTENT
        message = `${module}|${cleanContent}`;
        
        // Don't classify these as errors since [ERR] is just a log category
        if (row.level === 'error') {
          row.level = 'info';
        }
      }
      
      return { ...row, message };
    });
  }, []);

  // Group related price collector logs together
  const groupPriceCollectorLogs = useCallback((logLines, parseRowFn) => {
    const grouped = [];
    let i = 0;
    
    while (i < logLines.length) {
      const currentLine = logLines[i];
      const parsed = parseRowFn(currentLine);
      
      if (!parsed) {
        i++;
        continue;
      }
      
      let message = String(currentLine.raw || '').trim();
      
      // Clean timestamp from the message for pattern matching
      const timestampPrefixPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?\s*/;
      message = message.replace(timestampPrefixPattern, '');
      
      // Handle error messages - combine related error parts
      if (message.includes('Error fetching') && (message.includes('Client Error') || message.includes('Bad Request'))) {
        let combinedErrorMessage = message;
        let j = i + 1;
        
        // Look ahead for related error parts (Response, Unable to fetch)
        while (j < logLines.length && j < i + 3) {
          const nextRawMessage = String(logLines[j].raw || '').trim();
          const nextMessage = nextRawMessage.replace(timestampPrefixPattern, '');
          
          if (nextMessage.startsWith('Response:') || nextMessage.startsWith('Unable to fetch')) {
            combinedErrorMessage += ` | ${nextMessage}`;
            j++;
          } else {
            break;
          }
        }
        
        const cleanMessage = combinedErrorMessage.replace(/\s+/g, ' ').trim();
        
        grouped.push({
          seq: currentLine.seq,
          time: formatTs(currentLine.ts),
          level: 'error',
          message: `ERROR | ${cleanMessage}`,
          ts: currentLine.ts
        });
        
        i = j; // Skip the lines we've already processed
        continue;
      }
      
      // Handle data grouping - combine "Data for" with its fields (note: lowercase!)
      if (message.startsWith('Data for')) {
        let dataMessage = message;
        let j = i + 1;
        const dataFields = [];
        
        // Collect related data fields (note: correct capitalization!)
        while (j < logLines.length && j < i + 15) { // Look at next several lines
          const nextRawMessage = String(logLines[j].raw || '').trim();
          // Clean timestamp from next message too!
          const nextMessage = nextRawMessage.replace(timestampPrefixPattern, '');
          if (nextMessage.match(/^(ProviderId:|StartCurrencyId:|EndCurrencyId:|Price:|Bid:|BidQuantity:|Ask:|AskQuantity:|ProviderUpdatedAt:|SavedAt:)/)) {
            dataFields.push(nextMessage);
            j++;
          } else {
            break;
          }
        }
        
        // Create summary data entry with ALL fields
        if (dataFields.length > 0) {
          const summary = dataFields.join(', ');
          dataMessage += ` → ${summary}`;
        }
        
        grouped.push({
          seq: currentLine.seq,
          time: formatTs(currentLine.ts),
          level: 'info',
          message: `DATA | ${dataMessage}`,
          ts: currentLine.ts
        });
        
        i = j; // Skip the lines we've already processed
        continue;
      }
      
      // Skip individual data field entries when they're not preceded by Data for
      // (they should have been grouped above)
      if (message.match(/^(ProviderId:|StartCurrencyId:|EndCurrencyId:|Price:|Bid:|BidQuantity:|Ask:|AskQuantity:|ProviderUpdatedAt:|SavedAt:)/)) {
        i++;
        continue; // Skip these individual entries
      }
      
      // Skip individual Response and Unable to fetch messages that were already grouped with errors
      if (message.startsWith('Response:') || message.startsWith('Unable to fetch')) {
        i++;
        continue; // Skip these - they should have been grouped with error messages
      }
      
      // Skip dash separator lines
      if (message.match(/^-+$/)) {
        i++;
        continue; // Skip dash lines
      }
      
      // Group Database saves, Songbird/Flare success messages together
      if (message.startsWith('Database saves for')) {
        let combinedMessage = message;
        let j = i + 1;
        const statusMessages = [];
        
        // Look for related Songbird/Flare success messages
        while (j < logLines.length && j < i + 5) {
          const nextRawMessage = String(logLines[j].raw || '').trim();
          const nextMessage = nextRawMessage.replace(timestampPrefixPattern, '');
          
          if (nextMessage.match(/^(Songbird:|Flare:)\s*(Success|Failed)/i)) {
            statusMessages.push(nextMessage);
            j++;
          } else {
            break;
          }
        }
        
        // Combine all status messages on one line
        if (statusMessages.length > 0) {
          combinedMessage += ` | ${statusMessages.join(', ')}`;
        }
        
        grouped.push({
          seq: currentLine.seq,
          time: formatTs(currentLine.ts),
          level: 'info',
          message: `STATUS | ${combinedMessage}`,
          ts: currentLine.ts
        });
        
        i = j; // Skip the lines we've already processed
        continue;
      }
      
      // For all other messages, use normal parsing
      grouped.push(parsed);
      i++;
    }
    
    return grouped.filter(row => row !== null);
  }, [formatTs]);

  const rows = useMemo(() => {
    if (!filteredLines || filteredLines.length === 0) return [];
    
    // For price-collector containers, implement grouping logic
    if (container && container.includes('price-collector')) {
      return groupPriceCollectorLogs(filteredLines, parseRow);
    }
    
    // For health-monitor-server containers, implement custom formatting
    if (container && container.includes('health-monitor-server')) {
      return formatHealthMonitorLogs(filteredLines, parseRow);
    }
    
    // Default processing for other containers
    return (filteredLines || []).map(parseRow).filter(row => row !== null);
  }, [filteredLines, parseRow, container, groupPriceCollectorLogs, formatHealthMonitorLogs]);
  const eventSrcRef = useRef(null);

  const colDefs = useMemo(() => ([
    { field: 'time', headerName: 'Time', minWidth: 200, maxWidth: 240, filter: 'agDateColumnFilter',
      valueGetter: (params) => new Date(params.data.ts || params.data.time),
      valueFormatter: (params) => params.data.time || formatTs(params.data.ts) },
    { field: 'level', headerName: 'Level', minWidth: 110, maxWidth: 130, cellRenderer: (p) => <SeverityBadge level={p.value} />, filter: 'agSetColumnFilter' },
    { field: 'message', headerName: 'Message', minWidth: 1000, flex: 3, wrapText: true, autoHeight: true, filter: 'agTextColumnFilter', cellClass: 'log-message-cell ag-cell-wrap-text',
      cellRenderer: (params) => {
        const message = params.value || '';
        // Check if message has the structured format with separators (SOURCE | CONTENT)
        const separatorPattern = /^([A-Z_/\.:\d]+)\|(.+)$/i;
        const match = message.match(separatorPattern);
        
        if (match) {
          const [, source, content] = match;
          return (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'flex', flexWrap: 'nowrap' }}>
              <span style={{ color: '#7dd3fc', flexShrink: 0, marginRight: '4px' }}>{source}</span>
              <span style={{ color: '#ffffff', flexShrink: 0, marginRight: '4px' }}> | </span>
              <span style={{ color: '#ffffff', flexGrow: 1 }}>{content}</span>
            </div>
          );
        }
        
        // Fallback for non-structured messages
        return <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#ffffff' }}>{message}</div>;
      }
    },
  ]), [formatTs]);

  useEffect(() => {
    // Load inventory to populate metadata; initialize selections only if unset
    let cancelled = false;
    (async () => {
      try {
        const inv = await fetchLogsInventory();
        const instances = inv?.instances || [];
        if (cancelled) return;
        setInventory(instances);
        const uniqEntities = Array.from(new Set(instances.map((i)=> i.entity).filter(Boolean)));
        setEntityList(uniqEntities);
        const inst = instances.find((i) => i.instanceId === instanceId);
        if (inst) {
          setMeta({ chain: inst.chain || '', entity: inst.entity || '' });
          if (!selEntity) setSelEntity(inst.entity || '');
          if (!selChain) setSelChain(inst.chain || '');
          if (!selInstance) setSelInstance(instanceId);
          if (!selContainer) { setSelContainer(currentContainer); setStickyContainer(currentContainer); }
        } else {
          // keep user selections; just indicate suspended until a valid choice is made
          setViewSuspended(true);
        }
      } catch {}
    })();

    const load = async () => {
      try {
        const { lines } = await fetchLogsTail({ instanceId, container, limit: 2000 });
        setLines(lines || []);
      } catch {}
    };
    load();
    // Live updates via SSE
    try {
      // If suspended, close any existing stream and clear lines
      if (viewSuspended) {
        try { if (eventSrcRef.current) { eventSrcRef.current.close(); eventSrcRef.current = null; } } catch {}
        setLines([]);
      } else {
        const API_BASE = getApiBase();
        const qs = new URLSearchParams({ instanceId, container });
        const es = new EventSource(`${API_BASE}/api/logs/events?${qs.toString()}`);
        eventSrcRef.current = es;
        es.addEventListener('inventory', (e) => {
          try {
            const payload = JSON.parse(e.data);
            const instances = payload?.instances || [];
            setInventory(instances);
            // Do not override user-selected chain/entity/instance here
            const chains = Array.from(new Set(instances.filter((i)=> !selEntity || i.entity===selEntity).map((i)=> i.chain).filter(Boolean)));
            setChainList(chains);
            const insts = instances.filter((i)=> (!selEntity || i.entity===selEntity) && (!selChain || i.chain===selChain)).map((i)=> i.instanceId);
            setInstanceList(insts);
            if (selInstance) {
              const instSel = instances.find((i)=> i.instanceId === selInstance);
              setContainerList((instSel?.containers || []).map((c)=> c.name));
            }
          } catch {}
        });
        es.addEventListener('logLine', (e) => {
          try {
            const payload = JSON.parse(e.data);
            // Only append if this is our target (server already filters, but double-check)
            if (payload.instanceId === instanceId && payload.container === container) {
              // If user is at bottom, update immediately
              // If user scrolled up, store in pending
              if (isAtBottomRef.current) {
                setLines((prev) => {
                  const next = prev.slice(-1999);
                  next.push(payload);
                  return next;
                });
              } else {
                setPendingLines((prev) => [...prev, payload]);
              }
            }
          } catch {}
        });
        es.addEventListener('containerStatus', (e) => {
          // optionally handle container status changes here (e.g., show connection state)
        });
        es.addEventListener('instanceStatus', (e) => {
          // optionally handle instance status changes
        });
        es.onerror = () => {
          try { es.close(); } catch {}
          eventSrcRef.current = null;
        };
      }
    } catch {}
    return () => {
      try { if (eventSrcRef.current) { eventSrcRef.current.close(); eventSrcRef.current = null; } } catch {}
      cancelled = true;
    };
  }, [instanceId, container]);

  // Recalculate dependent lists whenever selection or inventory changes
  useEffect(() => {
    try {
      if (!Array.isArray(inventory) || inventory.length === 0) return;
      const nextChains = Array.from(new Set(inventory
        .filter((i) => !selEntity || i.entity === selEntity)
        .map((i) => i.chain)
        .filter(Boolean)));
      setChainList(nextChains);

      const nextInstances = inventory
        .filter((i) => (!selEntity || i.entity === selEntity) && (!selChain || i.chain === selChain))
        .map((i) => i.instanceId);
      setInstanceList(nextInstances);

      if (selInstance) {
        const instSel = inventory.find((i) => i.instanceId === selInstance);
        setContainerList((instSel?.containers || []).map((c) => c.name));
      } else {
        setContainerList([]);
      }
    } catch {}
  }, [inventory, selEntity, selChain, selInstance]);

  useEffect(() => {
    if (!gridApiRef.current) return;
    try {
      const api = gridApiRef.current;
      if (typeof api.setQuickFilter === 'function') api.setQuickFilter(search);
      else if (typeof api.setGridOption === 'function') api.setGridOption('quickFilterText', search);
      
      // Only auto-scroll if user is at the bottom
      if (isAtBottom && rows.length > 0) {
        api.ensureIndexVisible(rows.length - 1, 'bottom');
      }
    } catch {}
  }, [search, rows, isAtBottom]);

  const getRowHeight = useCallback((params) => {
    try {
      const message = String(params?.data?.message || '');
      const approxCharsPerLine = 180; // tuned for ~600px column width
      const base = 45; // baseline for one line
      const lineHeight = 18; // per wrapped line
      const linesByLen = Math.ceil(message.length / approxCharsPerLine);
      const linesByNewlines = (message.match(/\n/g) || []).length + 1;
      const lines = Math.max(linesByLen, linesByNewlines);
      const clamped = Math.max(1, Math.min(lines, 6));
      return base + (clamped - 1) * lineHeight;
    } catch {
      return 45;
    }
  }, []);

  return (
    <div className="main-wrapper runtime-log-detail">
      <div className="header-row">
        <div />
        <Link to="/runtime-logs" className="btn-logs text-button">Back</Link>
      </div>
      <div className="meta-grid">
        <div className={`meta-item meta-entity ${isEntityOpen ? 'open' : ''}`}>
          <div className="meta-label">ENTITY</div>
          <div className="meta-value">
            <button className={`meta-dd-trigger ${isEntityOpen ? 'open' : ''}`} onClick={(e)=>{ e.stopPropagation(); setIsEntityOpen((v)=>!v); setIsChainOpen(false); setIsInstanceOpen(false); setIsContainerOpen(false); }}>
              <EntityCellRenderer value={selEntity || meta.entity || '-'} />
              <span className={`meta-dd-chevron ${isEntityOpen ? 'open' : ''}`} aria-hidden>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9L12 15L18 9" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </button>
          </div>
          {isEntityOpen && (
            <div className="meta-dd-panel">
              {(entityList || []).map((name) => (
                <div key={name} className={`meta-dd-item ${name === selEntity ? 'active' : ''}`} onClick={()=>{
                  setSelEntity(name);
                  setSelChain('');
                  setSelInstance('');
                  setSelContainer('');
                  setIsEntityOpen(false);
                  setIsChainOpen(false);
                  setIsInstanceOpen(false);
                  setIsContainerOpen(false);
                  setContainerList([]);
                  setViewSuspended(true);
                }}>{name}</div>
              ))}
            </div>
          )}
        </div>
        <div className={`meta-item meta-chain ${isChainOpen ? 'open' : ''}`}>
          <div className="meta-label">CHAIN</div>
          <div className="meta-value">
            <button className={`meta-dd-trigger ${isChainOpen ? 'open' : ''}`} onClick={(e)=>{ e.stopPropagation(); setIsChainOpen((v)=>!v); setIsEntityOpen(false); setIsInstanceOpen(false); setIsContainerOpen(false); }}>
              <NetworkCellRenderer value={selChain || meta.chain || '-'} />
              <span className={`meta-dd-chevron ${isChainOpen ? 'open' : ''}`} aria-hidden>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9L12 15L18 9" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </button>
          </div>
          {isChainOpen && (
            <div className="meta-dd-panel">
              {(chainList || []).map((name) => (
                <div key={name} className={`meta-dd-item ${name === selChain ? 'active' : ''}`} onClick={()=>{
                  setSelChain(name);
                  setSelInstance('');
                  setSelContainer('');
                  setIsChainOpen(false);
                  setIsInstanceOpen(false);
                  setIsContainerOpen(false);
                  setContainerList([]);
                  setViewSuspended(true);
                }}>{name}</div>
              ))}
            </div>
          )}
        </div>
        <div className={`meta-item meta-instance ${isInstanceOpen ? 'open' : ''}`}>
          <div className="meta-label">INSTANCE</div>
          <div className="meta-value" title={selInstance || decodeURIComponent(instanceId)}>
            <button className={`meta-dd-trigger ${isInstanceOpen ? 'open' : ''} ${(!selEntity || (instanceList||[]).length===0) ? 'disabled' : ''}`} disabled={!selEntity || (instanceList||[]).length===0} onClick={(e)=>{ e.stopPropagation(); if (selEntity && (instanceList||[]).length>0) { setIsInstanceOpen((v)=>!v); setIsEntityOpen(false); setIsChainOpen(false); setIsContainerOpen(false); } }}>
              <span className={`${selInstance ? '' : 'placeholder'}`}>{selInstance || 'Select an instance'}</span>
              <span className={`meta-dd-chevron ${isInstanceOpen ? 'open' : ''}`} aria-hidden>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9L12 15L18 9" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </button>
          </div>
          {isInstanceOpen && (
            <div className="meta-dd-panel">
              {(instanceList || []).map((name) => (
                <div key={name} className={`meta-dd-item ${name === selInstance ? 'active' : ''}`} onClick={()=>{
                  setSelInstance(name);
                  setIsInstanceOpen(false);
                  setIsContainerOpen(false);
                  const instSel = inventory.find((i)=> i.instanceId === name);
                  const nextContainers = (instSel?.containers || []).map((c)=> c.name);
                  setContainerList(nextContainers);
                  if (stickyContainer && nextContainers.includes(stickyContainer)) {
                    setSelContainer(stickyContainer);
                    setViewSuspended(false);
                    navigate(`/runtime-logs/${encodeURIComponent(name)}/${encodeURIComponent(stickyContainer)}`);
                  } else {
                    setSelContainer('');
                    setViewSuspended(true);
                  }
                }}>{name}</div>
              ))}
            </div>
          )}
        </div>
        <div className={`meta-item meta-container ${isContainerOpen ? 'open' : ''}`}>
          <div className="meta-label">CONTAINER</div>
          <div className="meta-value" title={selContainer || currentContainer}>
            <button className={`meta-dd-trigger ${isContainerOpen ? 'open' : ''} ${!selInstance ? 'disabled' : ''}`} disabled={!selInstance} onClick={(e)=>{ e.stopPropagation(); if (selInstance) { setIsContainerOpen((v)=>!v); setIsEntityOpen(false); setIsChainOpen(false); setIsInstanceOpen(false); } }}>
              <span className={`meta-dd-text ${selInstance ? '' : 'placeholder'}`}>{selContainer || 'Select a container'}</span>
              <span className={`meta-dd-chevron ${isContainerOpen ? 'open' : ''}`} aria-hidden>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 9L12 15L18 9" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </button>
          </div>
          {isContainerOpen && selInstance && (
            <div className="meta-dd-panel">
              {(containerList || []).map((name) => (
                <div
                  key={name}
                  className={`meta-dd-item ${name === currentContainer ? 'active' : ''}`}
                  onClick={() => { setIsContainerOpen(false); setSelContainer(name); setStickyContainer(name); setViewSuspended(false); navigate(`/runtime-logs/${encodeURIComponent(selInstance || instanceId)}/${encodeURIComponent(name)}`); }}
                  title={name}
                >
                  {name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="grid-wrapper" style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
        <div className="logs-controls" style={{ padding: '0 15px' }}>
          <div style={{ position: 'relative', display: 'inline-block', width: '1000px' }}>
            <svg 
              style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                color: '#9ca3af',
                pointerEvents: 'none',
                zIndex: 1
              }}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input 
              className="search-input enhanced" 
              placeholder="Search logs..." 
              value={search} 
              onChange={(e)=>setSearch(e.target.value)}
              style={{ paddingLeft: '46px', paddingRight: search ? '30px' : '12px', width: '100%' }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  fontSize: '16px',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#374151'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <div className="filters">
            <label style={{ cursor: 'pointer' }}>
              <input type="radio" name="severity" value="all" checked={severity==='all'} onChange={()=>setSeverity('all')} style={{ cursor: 'pointer' }} />
              <span style={{ marginLeft: 6 }}>ALL</span>
            </label>
            <label style={{ cursor: 'pointer' }}>
              <input type="radio" name="severity" value="errors" checked={severity==='errors'} onChange={()=>setSeverity('errors')} style={{ cursor: 'pointer' }} />
              <span style={{ marginLeft: 6 }}>ERRORS</span>
            </label>
            <label style={{ cursor: 'pointer' }}>
              <input type="radio" name="severity" value="warnings" checked={severity==='warnings'} onChange={()=>setSeverity('warnings')} style={{ cursor: 'pointer' }} />
              <span style={{ marginLeft: 6 }}>WARNINGS</span>
            </label>
          </div>
        </div>
        <div className="grid-container" style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
          {viewSuspended && (
            <div className="grid-overlay" aria-hidden>
              <div className="empty-hint">{placeholderMsg}</div>
            </div>
          )}
          <div className="ag-theme-alpine-dark custom-dark-theme" style={{ width: '100%', height: '100%', position: 'relative' }}>
            {showJumpToBottom && (
              <button
                onClick={() => {
                  // Apply any pending lines first
                  if (pendingLines.length > 0) {
                    setLines((prev) => {
                      const combined = [...prev, ...pendingLines];
                      return combined.slice(-2000); // Keep last 2000 lines
                    });
                    setPendingLines([]);
                  }
                  
                  // Then scroll to bottom
                  if (gridApiRef.current) {
                    // Use setTimeout to ensure the new data is rendered first
                    setTimeout(() => {
                      if (gridApiRef.current) {
                        gridApiRef.current.ensureIndexVisible(rows.length - 1, 'bottom');
                      }
                    }, 50);
                  }
                  
                  setIsAtBottom(true);
                  isAtBottomRef.current = true;
                  setShowJumpToBottom(false);
                }}
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  right: '20px',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  border: 'none',
                  cursor: 'pointer',
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.2)';
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                  e.target.style.transform = 'scale(1)';
                }}
                title="Jump to latest logs"
              >
                <ArrowDownIcon width={24} height={24} />
              </button>
            )}
            <AgGridReact
              onGridReady={(p)=>{ 
                gridApiRef.current = p.api; 
                if (rows.length > 0) {
                  p.api.ensureIndexVisible(rows.length - 1, 'bottom'); 
                }
              }}
              onBodyScroll={(params) => {
                if (!params.api) return;
                
                try {
                  const lastRowIndex = params.api.getDisplayedRowCount() - 1;
                  if (lastRowIndex >= 0) {
                    // Use a simpler approach: check if the last row is rendered and visible
                    const renderedRows = params.api.getRenderedNodes();
                    const lastRenderedRow = renderedRows[renderedRows.length - 1];
                    
                    if (lastRenderedRow) {
                      // Check if the last rendered row is actually the last row in the dataset
                      const isLastRowRendered = lastRenderedRow.rowIndex === lastRowIndex;
                      

                      
                      if (isLastRowRendered !== isAtBottom) {
                        setIsAtBottom(isLastRowRendered);
                        isAtBottomRef.current = isLastRowRendered;
                        setShowJumpToBottom(!isLastRowRendered);
                        
                        // If user scrolled back to bottom naturally, apply pending lines
                        if (isLastRowRendered && pendingLines.length > 0) {
                          setLines((prev) => {
                            const combined = [...prev, ...pendingLines];
                            return combined.slice(-2000);
                          });
                          setPendingLines([]);
                        }
                      }
                    }
                  }
                } catch (e) {
                  console.warn('Scroll detection error:', e);
                }
              }}
              rowData={viewSuspended ? [] : rows}
              columnDefs={colDefs}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              domLayout="normal"
              rowHeight={45}
              getRowHeight={getRowHeight}
              headerHeight={50}
              getRowId={(params)=> String(params.data.seq)}
              pagination={false}
              ensureDomOrder={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}


